import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ModalController } from '@ionic/angular';
import { filter, takeUntil, take } from 'rxjs/operators';
import { combineLatest, Subject, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FEATURES } from '@shared/constants/splitio-features';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { ModalService } from './modal/modal.service';
import { SimpleActionModalComponent } from '@shared/components/simple-action-modal/simple-action-modal.component';
import { PATHS } from '@shared/constants/paths';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private updateCheckInProgress = false;
  private isTouch = false;
  private modalPresented = false;
  private isPunchoutOrPunchthruLogin = false;
  updateFeatureFlag$ = this.panAppState.feature$([
    FEATURES.split_dynamic_swupdate.name,
  ]);
  constructor(
    private swUpdate: SwUpdate,
    private translateService: TranslateService,
    private panAppState: PanAppState,
    private modalService: ModalService,
    private modalController: ModalController,
    private platformService: PlatformService,
    private router: Router,
  ) {}

  /**
   * Initialize the service worker update mechanism and perform initial check for updates.
   * This will only work if:
   * 1. The split_dynamic_swupdate feature flag is enabled
   * 2. SwUpdate.isEnabled returns true
   * 3. The device is not a touch device
   * 4. User logged in via punchout or punchthru
   */

  init(): void {
    console.log(`[AppUpdateService] Initializing app update service`);

    combineLatest([
      this.updateFeatureFlag$,
      this.platformService.isTouchExperience$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ([featureEnabled, isTouch]) => {
        this.isTouch = isTouch;
        if (this.isTouch) {
          console.log(
            `[AppUpdateService] Device is not desktop, ignoring update checks`,
          );
          return;
        }

        this.isPunchoutOrPunchthruLogin = await this.isPunchoutOrPunchthru(
          this.router.routerState.snapshot.root,
        );
        if (featureEnabled) {
          this.listenForVersionUpdates(this.isPunchoutOrPunchthruLogin);
          this.checkForUpdates();
        }
      });
  }

  //Listen for version updates
  //Show the update alert or refresh the page when an update is available
  private listenForVersionUpdates(isPunchoutOrPunchthruLogin: boolean) {
    if (this.isSwUpdateEnabled()) {
      this.swUpdate.versionUpdates
        .pipe(
          filter(event => event.type === 'VERSION_READY'),
          takeUntil(this.destroy$),
        )
        .subscribe((event: VersionReadyEvent) => {
          // Do not refresh if user entered via punchout/punchthru
          if (!isPunchoutOrPunchthruLogin) {
            this.handleUpdate();
          } else {
            console.log(
              "[AppUpdateService] Punchout/punchthru login. Won't refresh.",
            );
          }
        });
    }
  }

  /**
   * Check for updates using service worker (if enabled)
   * If an update is available, show the update alert
   * This method is called on app initialization and on app foreground event
   */
  checkForUpdates(): Promise<boolean> {
    if (this.isTouch || !this.isSwUpdateEnabled()) {
      return Promise.resolve(false);
    }

    const timestamp = new Date().getTime();
    console.log(
      `[AppUpdateService] Checking for app updates at ${timestamp}...`,
    );

    if (this.updateCheckInProgress) {
      console.log(
        `[AppUpdateService] Update check already in progress, skipping...`,
      );
      return Promise.resolve(false);
    } else {
      this.updateCheckInProgress = true;

      // Check if the split_dynamic_swupdate feature flag is enabled before checking for updates
      return this.panAppState
        .featureEnabled(FEATURES.split_dynamic_swupdate.name)
        .then(featureEnabled => {
          if (!featureEnabled) {
            this.updateCheckInProgress = false;
            return false;
          }
          return this.swUpdate
            .checkForUpdate()
            .then(updateAvailable => {
              this.updateCheckInProgress = false;
              console.log(
                `[AppUpdateService] Update check completed. Update available:`,
                updateAvailable,
              );
              return updateAvailable;
            })
            .catch(error => {
              this.updateCheckInProgress = false;
              console.error(
                `[AppUpdateService] Error checking for updates at ${timestamp}:`,
                error,
              );
              return false;
            });
        });
    }
  }

  private async handleUpdate(): Promise<void> {
    if (
      this.isTouch ||
      this.modalPresented ||
      this.isPunchoutOrPunchthruLogin
    ) {
      return;
    }

    // Check if the split_dynamic_swupdate_ui feature flag is enabled
    const featureEnabled = await this.panAppState.featureEnabled(
      FEATURES.split_dynamic_swupdate_ui.name,
    );

    // If the feature flag is on, show a refresh modal
    // If the feature flag is off, reload the page directly without showing the modal
    if (!featureEnabled) {
      window.location.reload();
      return;
    } else {
      this.modalPresented = true;

      this.modalService.setModalOptions(
        false,
        SimpleActionModalComponent,
        {
          title: this.translateService.instant(
            'i18n.appUpdate.updateAvailable',
          ),
          content: this.translateService.instant(
            'i18n.appUpdate.updateMessage',
          ),
          rightButtonText: this.translateService.instant(
            'i18n.appUpdate.refresh',
          ),
          noDismissButton: true,
        },
        'simple-action-modal',
      );
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then((resp: any) => {
        this.modalPresented = false;
        if (resp.data?.dispatchAction) {
          window.location.reload();
        }
      });
      return await modal.present();
    }
  }

  // First check if the previous URL is a punchout or punchthru
  // If not, check the route tree
  async isPunchoutOrPunchthru(routeOrSnapshot: any): Promise<boolean> {
    const punchoutPaths = [
      PATHS.PUNCHOUT,
      PATHS.LOGIN_PUNCHOUT,
      PATHS.LOGIN_PUNCHTHRU,
      PATHS.PUNCHTHRU,
    ];
    try {
      const previousUrl = await firstValueFrom(
        this.panAppState.previousUrl$.pipe(take(1)),
      );
      if (previousUrl && typeof previousUrl === 'string') {
        if (punchoutPaths.some(str => previousUrl.includes(str))) {
          return true;
        }
      }
    } catch (error) {
      console.error(`[AppUpdateService] Error checking previous URL:`, error);
    }
    return this.checkPunchoutOrPunchthruRouteTree(routeOrSnapshot);
  }

  private async checkPunchoutOrPunchthruRouteTree(
    routeOrSnapshot: any,
  ): Promise<boolean> {
    const snapshot = routeOrSnapshot.snapshot
      ? routeOrSnapshot.snapshot
      : routeOrSnapshot;
    const urlPath = snapshot.url.map(seg => seg.path).join('/');
    const fullPath = this.pathname;

    const punchoutPaths = [
      PATHS.PUNCHOUT,
      PATHS.LOGIN_PUNCHOUT,
      PATHS.LOGIN_PUNCHTHRU,
      PATHS.PUNCHTHRU,
    ];

    const isCurrentUrlPunchout = punchoutPaths.some(
      str => urlPath.includes(str) || fullPath.includes(str),
    );

    if (isCurrentUrlPunchout) {
      return true;
    }

    for (const child of snapshot.children) {
      if (await this.checkPunchoutOrPunchthruRouteTree(child)) {
        return true;
      }
    }
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isSwUpdateEnabled(): boolean {
    const isEnabled = this.swUpdate.isEnabled;
    if (!isEnabled) {
      console.log(
        '[AppUpdateService] SwUpdate is disabled, skipping update check',
      );
    }
    return isEnabled;
  }

  get pathname(): string {
    return window.location.pathname;
  }
}
